// ============================================================================
// Auth context — the app's source of truth for "who is logged in".
// ----------------------------------------------------------------------------
// Backed by GET /api/auth/me through React Query: on mount we fetch the current
// trainer; a 401 means logged-out (the query resolves to null rather than
// throwing up the tree). `login`/`logout` call the API and then refresh the
// `auth.me` query so the whole app reacts. The signed vf_session cookie is
// managed entirely by the server (httponly) — we never touch it directly.
// ============================================================================
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, authApi, qk } from "../api";
import type {
  LoginPayload,
  PasswordResetPayload,
  SignupPayload,
  SignupResponse,
  Trainer,
} from "../api";

interface AuthContextValue {
  trainer: Trainer | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (payload: LoginPayload) => Promise<Trainer>;
  /**
   * Register a new trainer and start their session. Returns the full signup
   * response (so the caller can branch on `email_verification_required`); the
   * `auth.me` cache is primed with the new trainer so the whole app reacts.
   */
  signup: (payload: SignupPayload) => Promise<SignupResponse>;
  /**
   * Wave 8 — consume a reset token + set a new password. The server signs the
   * trainer straight in (cookie); we prime `auth.me` with the returned trainer
   * so the reset flows directly into the app with no extra login.
   */
  reset: (payload: PasswordResetPayload) => Promise<Trainer>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const meQuery = useQuery<Trainer | null>({
    queryKey: qk.auth.me,
    queryFn: async ({ signal }) => {
      try {
        return await authApi.me(signal);
      } catch (err) {
        // Unauthenticated is a normal state, not an error to surface.
        if (err instanceof ApiError && err.isUnauthorized) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  const loginMutation = useMutation({
    mutationFn: (payload: LoginPayload) => authApi.login(payload),
    onSuccess: (res) => {
      qc.setQueryData(qk.auth.me, res.trainer);
    },
  });

  const login = useCallback(
    async (payload: LoginPayload) => {
      const res = await loginMutation.mutateAsync(payload);
      return res.trainer;
    },
    [loginMutation],
  );

  const signupMutation = useMutation({
    mutationFn: (payload: SignupPayload) => authApi.signup(payload),
    onSuccess: (res) => {
      // The server already set the session cookie — prime the cache so the app
      // treats the new trainer as authenticated with no extra round-trip.
      qc.setQueryData(qk.auth.me, res.trainer);
    },
  });

  const signup = useCallback(
    (payload: SignupPayload) => signupMutation.mutateAsync(payload),
    [signupMutation],
  );

  const resetMutation = useMutation({
    mutationFn: (payload: PasswordResetPayload) => authApi.reset(payload),
    onSuccess: (res) => {
      // The server already issued the session cookie — prime the cache so the
      // app treats the trainer as authenticated with no extra round-trip.
      qc.setQueryData(qk.auth.me, res.trainer);
    },
  });

  const reset = useCallback(
    async (payload: PasswordResetPayload) => {
      const res = await resetMutation.mutateAsync(payload);
      return res.trainer;
    },
    [resetMutation],
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    qc.setQueryData(qk.auth.me, null);
    // Drop every cached query so no other trainer's data lingers.
    qc.clear();
  }, [qc]);

  const value = useMemo<AuthContextValue>(
    () => ({
      trainer: meQuery.data ?? null,
      isLoading: meQuery.isLoading,
      isAuthenticated: !!meQuery.data,
      login,
      signup,
      reset,
      logout,
    }),
    [meQuery.data, meQuery.isLoading, login, signup, reset, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
